import { CustomValidationService } from './modules/validation/validation-service';
import { NativeValidationService } from './modules/validation/native-validation-service';
import { LoadingOverlayService } from './modules/ui/loading-overlay-service';
import { AddressService } from './modules/address/address-service';
import { CheckoutAddressService } from './modules/address/checkout-address-service';
import { PaymentService } from './modules/payment/payment-service';
import { FormService } from './modules/forms/form-service';
import { UIUtilities } from './modules/ui/ui-utilities';
import { initSwipers } from './modules/ui/swiper'
import { initLucideIcons } from './modules/ui/lucide-icons'
import { initDataNumbers } from './modules/ui/data-number'
import { initChoices } from './modules/ui/choices'
import { initFileDropzones } from './modules/ui/file-dropzone'
import { initBookingPage } from './modules/events/event-booking-service';
import { initCheckoutPage } from './modules/events/event-checkout-service';
import { initCourseBookingPage } from './modules/courses/course-booking-service';
import { initCourseCheckoutPage } from './modules/courses/course-checkout-service';

// Import styles and dependencies
import './scss/main.scss'
import 'bootstrap/dist/js/bootstrap.bundle.js'

console.log("Starting Frontend...");

// Application class that coordinates all modules
class App {
    private validationService: CustomValidationService;
    private addressService: AddressService;
    private checkoutAddressService: CheckoutAddressService;
    private paymentService: PaymentService;
    private formService: FormService;

    constructor() {
        this.validationService = new CustomValidationService();
        this.addressService = new AddressService();
        this.checkoutAddressService = new CheckoutAddressService();
        this.paymentService = new PaymentService();
        this.formService = new FormService();
    }

    public initialize(): void {
        // Initialize validation first
        this.validationService.initialize();
        
        // Initialize other services when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeServices();
            });
        } else {
            // DOM is already ready
            this.initializeServices();
        }
    }

    private initializeServices(): void {
        this.addressService.initialize();
        this.formService.initialize();
        
        // Set up global functions for backward compatibility
        this.setupGlobalFunctions();

        // Initialise UI widgets
        initSwipers();
        initLucideIcons();
        initDataNumbers();
        initChoices();
        initFileDropzones();

        // Only initialize checkout address service on checkout pages
        if (document.getElementById('EventBookingForm')
            || document.getElementById('CourseBookingForm')
            || document.getElementById('ECommerceForm')) {
            this.checkoutAddressService.initialize();
        }

        // Initialise Booking Page if applicable
        if (document.getElementById('EventBookingPageForm')) {
            initBookingPage();
        }

        // Initialise Course Booking Page if applicable
        if (document.getElementById('CourseBookingPageForm')) {
            initCourseBookingPage();
        }

        // Initialise Checkout Page if applicable
        if (document.getElementById('EventBookingForm')) {
            initCheckoutPage(this.paymentService);
        }

        // Initialise Course Checkout Page if applicable
        if (document.getElementById('CourseBookingForm') && document.getElementById('paymentMethodTabs')) {
            initCourseCheckoutPage(this.paymentService);
        }
    }

    private setupGlobalFunctions(): void {
        // Make payment functions available globally for Stripe integration
        window.initializeEventBookingPayment = this.paymentService.initializeEventBookingPayment.bind(this.paymentService);
        window.initializeCourseBookingPayment = this.paymentService.initializeCourseBookingPayment.bind(this.paymentService);
        window.initializeMembershipApplicationPayment = this.paymentService.initializeMembershipApplicationPayment.bind(this.paymentService);
        window.initializeECommercePayment = this.paymentService.initializeECommercePayment.bind(this.paymentService);
        
        // Make App object available globally for backward compatibility
        window.App = {
            defaults: {
                pageName: "Pagination.CurrentPage"
            },
            
            init: () => this.initialize(),
            
            initForms: () => this.formService.initialize(),
            
            smoothScrollTo: UIUtilities.smoothScrollTo,
            
            slideUp: UIUtilities.slideUp,
            
            slideDown: UIUtilities.slideDown,
            
            initSwitchList: UIUtilities.initSwitchList,
            
            // Modern services
            loadingOverlay: LoadingOverlayService.getInstance(),
            nativeValidation: NativeValidationService.getInstance(),
            
            // Legacy methods for backward compatibility
            performAjax: (element: HTMLFormElement, successFunc: Function, errorFunc: Function) => {
                // Delegate to form service's performAjax method
                (this.formService as any).performAjax(element, successFunc, errorFunc);
            },
            
            resetFormPage: (element: HTMLFormElement) => {
                (this.formService as any).resetFormPage(element);
            },
            
            changePage: (element: Element, pageNum?: number) => {
                (this.formService as any).changePage(element, pageNum);
            }
        };
    }
}



// Initialize the application
const app = new App();
app.initialize();

// Export for module usage if needed
//export default app;